// deps
import React, { memo, ReactElement, ReactNode, useCallback, useMemo, useEffect } from "react";
import classNames from "classnames";

// internal deps
import { BottomSheet, BottomSheetChildrenProps, isEmpty, priceInRupee } from "@swiggy-private/core-ui";

// styles
import styles from "../styles/RemovePlan.v4.scss";

// components
import { BottomSheetHeaderBg } from "@super-ui/widgets/bottom-sheet-header-bg";

// types
import {
    PlanDetailsItem,
    RemovePlanConfirmationHalfCard,
    RemovePlanConfirmationHalfCardButton,
} from "@swiggy-private/cart-types";
import { RemovePlanMessage } from "@super-ui/interfaces/SwiggyOne";

export enum BUTTON_CTA {
    CANCEL = "Don't Remove",
    REMOVE = "Remove Plan",
}

enum PLACEHOLDERS {
    SAVINGS_VALUE = "{{svg_value}}",
    EMOJI = "{{title_unicode}}",
}
export interface RemovePlanMessageProps {
    removePlanMessage: RemovePlanMessage;
    planDetails: PlanDetailsItem;
    onClose: VoidFunction;
    onRemove: (planName: string, planPrice: string) => void;
    impressionSuperRemovePlanConfirmationHalfCard?: ({
        savingsValue,
        title,
        message,
        subMessage,
        planId,
        variant,
        initialBillAmount,
        finalBillAmount,
    }: RemovePlanConfirmationHalfCard) => void;
    impressionSuperRemovePlanConfirmationHalfCardBackButton?: ({
        planName,
        planId,
        cta,
        variant,
        initialBillAmount,
        finalBillAmount,
    }: RemovePlanConfirmationHalfCardButton) => void;
    impressionSuperRemovePlanConfirmationHalfCardRemoveButton?: ({
        planName,
        planId,
        cta,
        variant,
        initialBillAmount,
        finalBillAmount,
    }: RemovePlanConfirmationHalfCardButton) => void;
    onClickSuperRemovePlanHalfCardBackButton?: ({
        planName,
        planId,
        cta,
        variant,
        initialBillAmount,
        finalBillAmount,
    }: RemovePlanConfirmationHalfCardButton) => void;
    onClickSuperRemovePlanHalfCardRemoveButton?: ({
        planName,
        planId,
        cta,
        variant,
        initialBillAmount,
        finalBillAmount,
    }: RemovePlanConfirmationHalfCardButton) => void;
}

function RemovePlanContent(props: BottomSheetChildrenProps & RemovePlanMessageProps): ReactElement {
    const {
        title: {
            text: titleText,
            attribute_values: { unicode: titleUnicode },
        },
        content: [
            {
                title: contentTitle,
                sub_title: subTitle,
                attribute_values: { svg_value: savingsValue },
            },
        ],
    } = props.removePlanMessage;
    const { plan_id: planId, name, final_price: finalPrice } = props.planDetails;
    const {
        onRemove,
        close,
        impressionSuperRemovePlanConfirmationHalfCard,
        impressionSuperRemovePlanConfirmationHalfCardBackButton,
        impressionSuperRemovePlanConfirmationHalfCardRemoveButton,
        onClickSuperRemovePlanHalfCardRemoveButton,
        onClickSuperRemovePlanHalfCardBackButton,
    } = props;

    const onRemoveClick = useCallback(() => {
        if (typeof onRemove === "function") {
            onRemove(name, priceInRupee(finalPrice));
            onClickSuperRemovePlanHalfCardRemoveButton?.({ planName: name, planId, cta: BUTTON_CTA.REMOVE });
            close();
        }
    }, [onRemove, name, finalPrice, onClickSuperRemovePlanHalfCardRemoveButton, planId, close]);

    const onCancelClick = useCallback(() => {
        onClickSuperRemovePlanHalfCardBackButton?.({ planName: name, planId, cta: BUTTON_CTA.REMOVE });
        close();
    }, [onClickSuperRemovePlanHalfCardBackButton, name, planId, close]);

    const displayTitle = useMemo(() => {
        let emoji = "";
        if (!isEmpty(titleUnicode)) {
            emoji = String.fromCodePoint(parseInt(titleUnicode));
        }
        return titleText.replace(PLACEHOLDERS.EMOJI, emoji);
    }, [titleText, titleUnicode]);

    const [widgetTitle, message] = useMemo(() => {
        let htmlDisplayTitle;
        if (savingsValue && contentTitle.indexOf(PLACEHOLDERS.SAVINGS_VALUE) !== -1) {
            const htmlTitle = contentTitle.replace(
                PLACEHOLDERS.SAVINGS_VALUE,
                `<span class=${classNames(styles.currency)}>${savingsValue}</span>`,
            );
            htmlDisplayTitle = (
                <div className={styles.contentContainerTitle} dangerouslySetInnerHTML={{ __html: htmlTitle }}></div>
            );
        } else {
            htmlDisplayTitle = <div className={styles.contentContainerTitle}>{contentTitle}</div>;
        }
        return [htmlDisplayTitle, contentTitle.replace(PLACEHOLDERS.SAVINGS_VALUE, savingsValue)];
    }, [contentTitle, savingsValue]);

    useEffect(() => {
        if (typeof impressionSuperRemovePlanConfirmationHalfCard === "function") {
            impressionSuperRemovePlanConfirmationHalfCard({
                savingsValue,
                title: displayTitle,
                message,
                subMessage: subTitle,
            });
        }
        if (typeof impressionSuperRemovePlanConfirmationHalfCardBackButton === "function") {
            impressionSuperRemovePlanConfirmationHalfCardBackButton?.({
                planName: name,
                planId,
                cta: BUTTON_CTA.CANCEL,
            });
        }
        if (typeof impressionSuperRemovePlanConfirmationHalfCardRemoveButton === "function") {
            impressionSuperRemovePlanConfirmationHalfCardRemoveButton?.({
                planName: name,
                planId,
                cta: BUTTON_CTA.REMOVE,
            });
        }
    }, [
        displayTitle,
        impressionSuperRemovePlanConfirmationHalfCard,
        impressionSuperRemovePlanConfirmationHalfCardBackButton,
        impressionSuperRemovePlanConfirmationHalfCardRemoveButton,
        message,
        name,
        planId,
        savingsValue,
        subTitle,
    ]);

    return (
        <div className={styles.removePlanContainer}>
            <div className={styles.header}>
                <BottomSheetHeaderBg className={styles.fullBackground}></BottomSheetHeaderBg>
                <div className={styles.headerTitle}>{displayTitle}</div>
            </div>
            <div className={styles.content}>
                <div className={styles.contentContainer}>
                    <div>{widgetTitle}</div>
                    <div className={styles.contentContainerBody}>{subTitle}</div>
                </div>
            </div>
            <div className={styles.footer}>
                <div className={styles.removePlanContainerActions}>
                    <button onClick={onCancelClick} className={styles.removePlanContainerActionsSecondaryButton}>
                        {BUTTON_CTA.CANCEL}
                    </button>
                    <button onClick={onRemoveClick} className={styles.removePlanContainerActionsPrimaryButton}>
                        {BUTTON_CTA.REMOVE}
                    </button>
                </div>
            </div>
        </div>
    );
}
function RemovePlan(props: RemovePlanMessageProps): ReactElement {
    const { onClose } = props;
    return (
        <BottomSheet onClose={onClose} hideCloseIcon={true} hideOnOverlayClick={true}>
            {(bProps: BottomSheetChildrenProps): ReactNode => <RemovePlanContent {...bProps} {...props} />}
        </BottomSheet>
    );
}

export default memo(RemovePlan);
